import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './CreatePostPage.css';
import { AppHeader, BottomNavigation, CreateOptionsMenu, ImagePreviewsGrid, BadWordsModal, BackButton, Button } from './shared';
import EmojiPicker from './EmojiPicker';
import { useNavigation } from '../contexts/NavigationContext';
import { useLanguage } from '../contexts/LanguageContext';
import useLogout from '../hooks/useLogout';
import {
  popoverContainerStyle,
  popoverHeaderStyle,
  popoverMessageStyle,
  popoverButtonContainerStyle,
  getCancelButtonStyle,
  getSubmitButtonStyle
} from './shared/PopoverStyles';

/**
 * Issue #101: CreatePostPage - Separate page for creating posts
 * Migrated from inline/popover to full page for better UX
 */
const CreatePostPage = ({ onPostCreated }) => {
  const navigation = useNavigation();
  const { t } = useLanguage();
  const { handleLogout, isLoggingOut, logoutError, clearLogoutError } = useLogout(navigation.onLogout);
  const [content, setContent] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showCreateOptions, setShowCreateOptions] = useState(false);
  const [userData, setUserData] = useState(null);
  // Issue #105: State for unsaved changes confirmation dialog
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  // Issue #161: State for bad words modal
  const [showBadWordsModal, setShowBadWordsModal] = useState(false);
  const [detectedBadWords, setDetectedBadWords] = useState([]);

  // Fetch user data on mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get('/api/user/me');
        setUserData(response.data);
      } catch (err) {
        console.error('Error fetching user data:', err);
      }
    };
    fetchUserData();
  }, []);

  // Issue #262: Prevent auto-focus and ensure page starts at top
  useEffect(() => {
    // Scroll to top of page immediately
    window.scrollTo(0, 0);

    // Prevent any input from receiving focus during initial load
    // This catches browser auto-focus behavior that happens after React render
    const preventFocus = (e) => {
      const target = e.target;
      // Only prevent focus on input elements (text, textarea, select)
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT')) {
        e.target.blur();
      }
    };

    // Listen for focus events during initial load period
    document.addEventListener('focusin', preventFocus, true);

    // Remove listener after page has fully loaded (300ms should be enough)
    const timer = setTimeout(() => {
      document.removeEventListener('focusin', preventFocus, true);
    }, 300);

    // Also blur any currently focused element
    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }

    return () => {
      document.removeEventListener('focusin', preventFocus, true);
      clearTimeout(timer);
    };
  }, []);

  const textareaRef = useRef(null);

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }, [content]);

  const MAX_FILES = 10;
  const MAX_FILE_SIZE = 5 * 1024 * 1024;
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];
  const MAX_IMAGE_DIMENSION = 1920;
  const COMPRESSION_QUALITY = 0.85;

  const isValidImageFile = (file) => {
    if (file.type && ALLOWED_TYPES.includes(file.type)) {
      return true;
    }
    const fileName = file.name.toLowerCase();
    return ALLOWED_EXTENSIONS.some(ext => fileName.endsWith(ext));
  };

  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      if (file.size <= MAX_FILE_SIZE) {
        resolve(file);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let width = img.width;
          let height = img.height;

          if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
            if (width > height) {
              height = (height / width) * MAX_IMAGE_DIMENSION;
              width = MAX_IMAGE_DIMENSION;
            } else {
              width = (width / height) * MAX_IMAGE_DIMENSION;
              height = MAX_IMAGE_DIMENSION;
            }
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });

                if (compressedFile.size > MAX_FILE_SIZE) {
                  reject(new Error('Bild ist zu groß (auch nach Komprimierung)'));
                } else {
                  resolve(compressedFile);
                }
              } else {
                reject(new Error('Fehler beim Komprimieren des Bildes'));
              }
            },
            'image/jpeg',
            COMPRESSION_QUALITY
          );
        };
        img.onerror = () => reject(new Error('Fehler beim Laden des Bildes'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Fehler beim Lesen des Bildes'));
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    setError(null);

    if (selectedFiles.length + files.length > MAX_FILES) {
      setError(`Maximal ${MAX_FILES} Bilder pro Post erlaubt`);
      return;
    }

    setIsCompressing(true);

    try {
      const validFiles = [];
      const newPreviews = [];

      for (const file of files) {
        if (!isValidImageFile(file)) {
          setError('Nur JPEG, PNG und WebP Bilder sind erlaubt');
          setIsCompressing(false);
          return;
        }

        try {
          const processedFile = await compressImage(file);

          if (processedFile.size > MAX_FILE_SIZE) {
            setError('Bild ist zu groß (max 5MB). Bitte wähle ein kleineres Bild.');
            setIsCompressing(false);
            return;
          }

          validFiles.push(processedFile);

          const reader = new FileReader();
          reader.onload = (event) => {
            newPreviews.push({
              id: Math.random(),
              src: event.target.result,
              file: processedFile
            });

            if (newPreviews.length === validFiles.length) {
              setPreviews(prev => [...prev, ...newPreviews]);
              setSelectedFiles(prev => [...prev, ...validFiles]);
              setIsCompressing(false);
            }
          };
          reader.readAsDataURL(processedFile);
        } catch (compressionError) {
          setError(compressionError.message || 'Fehler beim Verarbeiten des Bildes');
          setIsCompressing(false);
          return;
        }
      }
    } catch (err) {
      setError('Fehler beim Verarbeiten der Dateien');
      setIsCompressing(false);
    }

    e.target.value = '';
  };

  const removeImage = (previewId) => {
    const index = previews.findIndex(p => p.id === previewId);
    if (index > -1) {
      const newPreviews = previews.filter((_, i) => i !== index);
      const newFiles = selectedFiles.filter((_, i) => i !== index);
      setPreviews(newPreviews);
      setSelectedFiles(newFiles);
    }
  };

  const handleEmojiSelect = (emoji) => {
    if (textareaRef.current) {
      const start = textareaRef.current.selectionStart;
      const end = textareaRef.current.selectionEnd;
      const newContent = content.substring(0, start) + emoji + content.substring(end);
      setContent(newContent);

      setTimeout(() => {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(start + emoji.length, start + emoji.length);
      }, 0);
    } else {
      setContent(content + emoji);
    }
    setShowEmojiPicker(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!content.trim()) {
      setError('Beitrag kann nicht leer sein');
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('content', content);
      formData.append('is_anonymous', isAnonymous);

      selectedFiles.forEach((file) => {
        formData.append('images', file);
      });

      const response = await axios.post('/api/posts', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (onPostCreated) {
        onPostCreated(response.data);
      }

      // Navigate to "New Posts" tab so user can see their new post
      localStorage.setItem('landingPageTab', 'new');
      navigation.onNavigateToLandingPage();
    } catch (err) {
      let errorMsg = 'Fehler beim Erstellen des Posts';

      if (err.response) {
        // Issue #161: Check if bad words were detected - show modal instead of text error
        const badWords = err.response?.data?.bad_words;
        if (badWords && badWords.length > 0) {
          setDetectedBadWords(badWords);
          setShowBadWordsModal(true);
          // Don't set error text - modal handles the display
        } else {
          // Server responded with error - prioritize server message
          const serverMessage = err.response?.data?.message;

          if (serverMessage) {
            errorMsg = serverMessage;
          } else if (err.response.status === 413) {
            errorMsg = 'Datei ist zu groß. Bitte wähle kleinere Bilder.';
          } else if (err.response.status === 422) {
            errorMsg = 'Ungültige Daten. Bitte überprüfe deine Eingabe.';
          } else if (err.response.status === 401) {
            errorMsg = 'Sitzung abgelaufen. Bitte melde dich erneut an.';
          } else {
            errorMsg = `Server Fehler (${err.response.status})`;
          }
          setError(errorMsg);
        }
      } else if (err.request) {
        errorMsg = 'Keine Verbindung zum Server. Bitte überprüfe deine Internetverbindung.';
        setError(errorMsg);
      } else {
        errorMsg = err.message || 'Unbekannter Fehler beim Erstellen des Posts';
        setError(errorMsg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Issue #105: Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    return content.trim().length > 0 || selectedFiles.length > 0;
  };

  const handleBack = () => {
    // Issue #105: Show confirmation if there are unsaved changes
    if (hasUnsavedChanges()) {
      setShowLeaveConfirm(true);
    } else {
      navigation.onNavigateBack();
    }
  };

  // Issue #105: Confirm leave and navigate back
  const handleConfirmLeave = () => {
    setShowLeaveConfirm(false);
    navigation.onNavigateBack();
  };

  // Issue #105: Cancel leave and stay on page
  const handleCancelLeave = () => {
    setShowLeaveConfirm(false);
  };

  return (
    <div className="create-post-page">
      <AppHeader 
        userData={userData}
        onLogout={handleLogout}
        isLoggingOut={isLoggingOut}
        logoutError={logoutError}
        onClearLogoutError={clearLogoutError}
      />

      {/* Tab Navigation with title */}
      <div className="tab-navigation-create-post">
        <span className="tab-title">{t('createPost.pageTitle') || 'Einen neuen Beitrag erstellen'}</span>
      </div>

      {/* Main Content */}
      <div className="create-post-page-content">
        {/* Back Button - Issue #115: Added aria-label for accessibility */}
        <BackButton
          onClick={handleBack}
          className="back-button-create-post"
        />

        {/* Error Message - Issue #115: Added role="alert" for screen reader announcement */}
        {error && <div className="create-post-error-message" role="alert" aria-live="assertive">{error}</div>}

        <form onSubmit={handleSubmit} className="create-post-page-form">
          {/* Section Header */}
          <div className="create-post-section-header">
            <span className="section-icon">💬</span>
            <span className="section-title">{t('createPost.yourThoughts') || 'Deine Gedanken...'}</span>
          </div>

          {/* Text Field - Issue #115: Added aria-label for accessibility */}
          {/* Issue #262: Removed autoFocus to prevent keyboard from appearing on mobile before user taps input */}
          <div className="create-post-text-field">
            <textarea
              ref={textareaRef}
              className="create-post-textarea"
              placeholder={t('createPost.placeholder') || 'Was möchtest du teilen?'}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              aria-label={t('createPost.yourThoughts') || 'Deine Gedanken'}
            />
          </div>

          {/* Action Buttons Row - Issue #115: Improved accessibility */}
          <div className="create-post-actions-row">
            {/* Anonymity Toggle - Issue #115: Added proper label association */}
            <div className="create-post-toggle-container">
              <label className="create-post-anonymity-toggle" id="anonymity-toggle-label">
                <input
                  type="checkbox"
                  checked={isAnonymous}
                  onChange={(e) => setIsAnonymous(e.target.checked)}
                  disabled={isSubmitting}
                  aria-labelledby="anonymity-toggle-label anonymity-toggle-text"
                />
                <span className="create-post-toggle-switch" aria-hidden="true"></span>
              </label>
              <span className="create-post-toggle-label" id="anonymity-toggle-text">{t('createPost.anonymous') || 'Anonym'}</span>
            </div>

            {/* Emoji Button - Issue #115: Added aria-expanded */}
            <button
              type="button"
              className="create-post-action-button"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              aria-label={t('createPost.addEmoji') || 'Emoji hinzufügen'}
              aria-expanded={showEmojiPicker}
              aria-haspopup="dialog"
            >
              <span className="action-button-icon" aria-hidden="true">😊</span>
              <span className="action-button-text">{t('createPost.emoji') || 'Emoji'}</span>
            </button>
          </div>

          {/* Image Previews - between action buttons and add photos button */}
          <ImagePreviewsGrid
            previews={previews}
            onRemove={removeImage}
          />

          {/* Add Photos Button - Issue #115: Improved accessibility with proper labeling */}
          <label
            className="create-post-add-photos"
            aria-label={isCompressing ? (t('createPost.compressing') || 'Komprimiere...') : (t('createPost.addPhotos') || 'Bilder hinzufügen')}
            role="button"
            tabIndex={selectedFiles.length >= MAX_FILES || isSubmitting || isCompressing ? -1 : 0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.currentTarget.querySelector('input')?.click();
              }
            }}
          >
            <span className="add-photos-icon" aria-hidden="true">{isCompressing ? '⏳' : '📷'}</span>
            <span className="add-photos-text">{t('createPost.addPhotos') || 'Bilder hinzufügen (optional)'}</span>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              disabled={selectedFiles.length >= MAX_FILES || isSubmitting || isCompressing}
              className="create-post-file-input"
              aria-label={t('createPost.selectImages') || 'Bilder auswählen'}
            />
          </label>

          {/* Submit Button */}
          <div className="create-post-submit-wrapper">
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={!content.trim() || isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? (t('creating') || 'Erstelle...') : (t('createPost.create') || 'Erstellen')}
            </Button>
          </div>
        </form>

        {/* Emoji Picker */}
        {showEmojiPicker && (
          <EmojiPicker
            onEmojiSelect={handleEmojiSelect}
            onClose={() => setShowEmojiPicker(false)}
          />
        )}
      </div>

      <CreateOptionsMenu
        isOpen={showCreateOptions}
        onClose={() => setShowCreateOptions(false)}
      />
      <BottomNavigation
        activeTab="create"
        onCreateClick={() => setShowCreateOptions(!showCreateOptions)}
      />

      {/* Issue #105: Unsaved changes confirmation modal - Issue #115: Added accessibility attributes */}
      {showLeaveConfirm && (
        <div
          className="modal-overlay"
          onClick={handleCancelLeave}
          role="presentation"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={popoverContainerStyle}
            role="dialog"
            aria-modal="true"
            aria-labelledby="leave-confirm-title"
            aria-describedby="leave-confirm-message"
          >
            <h2 style={popoverHeaderStyle} id="leave-confirm-title">{t('unsavedChanges.title')}</h2>
            <p style={popoverMessageStyle} id="leave-confirm-message">{t('unsavedChanges.message')}</p>
            <div style={popoverButtonContainerStyle}>
              <button
                onClick={handleCancelLeave}
                style={getCancelButtonStyle(false)}
              >
                {t('unsavedChanges.stay')}
              </button>
              <button
                onClick={handleConfirmLeave}
                style={getSubmitButtonStyle(false)}
              >
                {t('unsavedChanges.leave')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Issue #161: Bad words modal */}
      <BadWordsModal
        isOpen={showBadWordsModal}
        onClose={() => setShowBadWordsModal(false)}
        badWords={detectedBadWords}
        contentType="post"
      />
    </div>
  );
};

export default CreatePostPage;
